import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Calendar, Clock, User, ArrowLeft, ArrowRight } from 'lucide-react';
import { blogPosts } from '../data/mock';

const BlogPost = () => {
  const { slug } = useParams();
  const post = blogPosts.find(p => p.slug === slug);
  
  if (!post) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Blog Post Not Found</h1>
          <Link to="/blog">
            <Button className="bg-gradient-to-r from-blue-600 to-cyan-500 text-white">
              <ArrowLeft className="mr-2 w-4 h-4" />
              Back to Blog
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const currentIndex = blogPosts.findIndex(p => p.slug === slug);
  const prevPost = currentIndex > 0 ? blogPosts[currentIndex - 1] : null;
  const nextPost = currentIndex < blogPosts.length - 1 ? blogPosts[currentIndex + 1] : null;

  // Full blog content based on topic
  const getFullContent = () => {
    switch(post.id) {
      case 1:
        return `
          <h2>The Rise of Generative AI in Business</h2>
          <p>Generative AI has emerged as one of the most transformative technologies of 2025, fundamentally changing how businesses operate across all sectors. From customer service to content creation, AI models like GPT-5 and Gemini are revolutionizing business processes.</p>
          
          <h3>Key Applications in Business</h3>
          <ul>
            <li><strong>Customer Service Automation:</strong> AI-powered chatbots are handling 80% of customer queries with human-like responses</li>
            <li><strong>Content Generation:</strong> Marketing teams are using AI to create personalized content at scale</li>
            <li><strong>Code Generation:</strong> Developers are becoming 40% more productive with AI coding assistants</li>
            <li><strong>Data Analysis:</strong> AI is processing vast datasets to provide actionable business insights</li>
          </ul>

          <h3>Impact on Business Operations</h3>
          <p>Organizations implementing generative AI are seeing remarkable results. Customer satisfaction scores have increased by an average of 35%, while operational costs have decreased by 25%. The technology is particularly effective in:</p>
          <ul>
            <li>Automating repetitive tasks</li>
            <li>Enhancing decision-making with data-driven insights</li>
            <li>Personalizing customer experiences</li>
            <li>Accelerating product development cycles</li>
          </ul>

          <h3>Challenges and Considerations</h3>
          <p>While the benefits are significant, businesses must address several challenges including data privacy concerns, integration with existing systems, and the need for proper AI governance frameworks.</p>

          <h3>Future Outlook</h3>
          <p>As AI models continue to evolve, we expect to see even more sophisticated applications emerge. Businesses that adopt AI early and strategically will have a significant competitive advantage in the coming years.</p>
        `;
      case 2:
        return `
          <h2>Multi-Cloud and Edge Computing: The New Normal</h2>
          <p>The cloud computing landscape is evolving rapidly, with multi-cloud strategies and edge computing becoming essential components of modern IT infrastructure.</p>

          <h3>Why Multi-Cloud?</h3>
          <p>Organizations are increasingly adopting multi-cloud approaches to:</p>
          <ul>
            <li>Avoid vendor lock-in and maintain flexibility</li>
            <li>Optimize costs by selecting best-of-breed services</li>
            <li>Improve reliability through redundancy</li>
            <li>Meet data sovereignty and compliance requirements</li>
          </ul>

          <h3>The Edge Computing Revolution</h3>
          <p>Edge computing brings processing power closer to data sources, enabling:</p>
          <ul>
            <li>Real-time data processing with minimal latency</li>
            <li>Reduced bandwidth costs</li>
            <li>Enhanced privacy and security</li>
            <li>Support for IoT devices at scale</li>
          </ul>

          <h3>Implementation Best Practices</h3>
          <p>Successful multi-cloud and edge computing implementations require careful planning, robust management tools, and a clear migration strategy.</p>
        `;
      case 3:
        return `
          <h2>Cybersecurity Threats in 2025: A Comprehensive Guide</h2>
          <p>The cybersecurity landscape has become increasingly complex, with new threats emerging daily. Organizations must stay vigilant and proactive to protect their digital assets.</p>

          <h3>Emerging Threat Vectors</h3>
          <ul>
            <li><strong>AI-Powered Attacks:</strong> Hackers are using AI to create more sophisticated phishing campaigns and malware</li>
            <li><strong>Ransomware Evolution:</strong> Double and triple extortion tactics are becoming common</li>
            <li><strong>Supply Chain Attacks:</strong> Compromising third-party vendors to breach target organizations</li>
            <li><strong>Cloud Security Risks:</strong> Misconfigured cloud services remain a major vulnerability</li>
          </ul>

          <h3>Essential Protection Strategies</h3>
          <p>To defend against modern threats, organizations should implement:</p>
          <ul>
            <li>Zero-trust security architecture</li>
            <li>Regular security awareness training</li>
            <li>Continuous vulnerability assessments</li>
            <li>Incident response planning</li>
            <li>Multi-factor authentication everywhere</li>
          </ul>

          <h3>Compliance and Regulations</h3>
          <p>Understanding and adhering to regulations like GDPR, HIPAA, and industry-specific standards is crucial for avoiding penalties and maintaining customer trust.</p>
        `;
      default:
        return `
          <h2>${post.title}</h2>
          <p>${post.excerpt}</p>
          
          <h3>Introduction</h3>
          <p>This article explores the latest trends and developments in ${post.category.toLowerCase()}, providing valuable insights for businesses and professionals looking to stay ahead in the rapidly evolving technology landscape.</p>

          <h3>Key Takeaways</h3>
          <ul>
            <li>Understanding the fundamentals and core concepts</li>
            <li>Practical applications in real-world scenarios</li>
            <li>Best practices for implementation</li>
            <li>Future trends and predictions</li>
          </ul>

          <h3>Industry Impact</h3>
          <p>The developments in this area are having a significant impact across various industries, from healthcare to finance, transforming how businesses operate and deliver value to customers.</p>

          <h3>Getting Started</h3>
          <p>For organizations looking to leverage these technologies, it's important to start with a clear strategy, invest in the right tools and talent, and maintain a focus on continuous learning and adaptation.</p>

          <h3>Conclusion</h3>
          <p>As technology continues to evolve at an unprecedented pace, staying informed and adapting quickly will be key to maintaining a competitive edge in the market.</p>
        `;
    }
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section with Featured Image */}
      <section className="relative h-96 overflow-hidden">
        <img
          src={post.image}
          alt={post.title}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent"></div>
        
        <div className="absolute bottom-0 left-0 right-0 p-8">
          <div className="max-w-4xl mx-auto">
            <Badge className="bg-blue-600 text-white mb-4">{post.category}</Badge>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
              {post.title}
            </h1>
            <div className="flex items-center gap-6 text-white/90">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                <span>{post.author}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>{new Date(post.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>{post.readTime}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Content */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="prose prose-lg max-w-none">
            <div dangerouslySetInnerHTML={{ __html: getFullContent() }} />
          </div>

          {/* Share Section */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Share this article</h3>
            <div className="flex gap-4">
              <button className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200">
                Share on LinkedIn
              </button>
              <button className="px-6 py-3 bg-blue-400 text-white rounded-lg hover:bg-blue-500 transition-colors duration-200">
                Share on Twitter
              </button>
              <button className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200">
                Share on WhatsApp
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation to Previous/Next Posts */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {prevPost && (
              <Link to={`/blog/${prevPost.slug}`} className="group">
                <div className="flex items-center space-x-4 p-6 bg-white rounded-lg hover:shadow-lg transition-all duration-300 border-2 hover:border-blue-200">
                  <ArrowLeft className="w-8 h-8 text-blue-600 flex-shrink-0" />
                  <div>
                    <div className="text-sm text-gray-500 mb-1">Previous Post</div>
                    <div className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors duration-200">
                      {prevPost.title}
                    </div>
                  </div>
                </div>
              </Link>
            )}
            {nextPost && (
              <Link to={`/blog/${nextPost.slug}`} className="group">
                <div className="flex items-center space-x-4 p-6 bg-white rounded-lg hover:shadow-lg transition-all duration-300 border-2 hover:border-blue-200">
                  <div className="flex-1">
                    <div className="text-sm text-gray-500 mb-1">Next Post</div>
                    <div className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors duration-200">
                      {nextPost.title}
                    </div>
                  </div>
                  <ArrowRight className="w-8 h-8 text-blue-600 flex-shrink-0" />
                </div>
              </Link>
            )}
          </div>

          <div className="mt-8 text-center">
            <Link to="/blog">
              <Button variant="outline" className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50">
                <ArrowLeft className="mr-2 w-4 h-4" />
                Back to All Articles
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPost;
